/**
 * PHP Unserializer Utility
 * Handles PHP serialized data conversion to JavaScript arrays/objects
 */

/**
 * Simple PHP unserialize implementation for basic arrays
 * This handles the most common PHP array formats we encounter
 */
export const unserializePhpArray = (serializedString) => {
    if (!serializedString || typeof serializedString !== 'string') {
        return [];
    }

    try {
        // First try to parse as JSON (if it was already converted)
        const jsonParsed = JSON.parse(serializedString);
        if (Array.isArray(jsonParsed)) return jsonParsed;
        if (typeof jsonParsed === 'object') return Object.values(jsonParsed);
        return [];
    } catch {
        // Not JSON, try PHP unserialize
    }

    try {
        // Handle simple PHP array format: a:n:{i:0;s:x:"value";...}
        if (serializedString.startsWith('a:')) {
            const result = [];
            
            // Extract array content between {}
            const match = serializedString.match(/^a:\d+:\{(.+)\}$/s);
            if (!match) return [];
            
            const content = match[1];
            let pos = 0;
            
            while (pos < content.length) {
                // Skip whitespace
                while (pos < content.length && /\s/.test(content[pos])) pos++;
                
                // Look for index marker: i:N;
                const indexMatch = content.substring(pos).match(/^i:\d+;/);
                if (!indexMatch) break;
                pos += indexMatch[0].length;
                
                // Skip whitespace
                while (pos < content.length && /\s/.test(content[pos])) pos++;
                
                // Look for value type
                if (content.substring(pos).startsWith('s:')) {
                    // String value: s:BYTE_LENGTH:"VALUE";
                    // PHP serialize() stores byte length (UTF-8), but JS substring() counts
                    // characters (UTF-16). Multi-byte chars (e.g. ™ = 3 bytes, 1 char) make
                    // the byte count larger than the JS character count, causing overshoot.
                    // Fix: advance character-by-character until we have consumed exactly
                    // byteLength UTF-8 bytes, then verify the closing delimiter.
                    const lengthMatch = content.substring(pos).match(/^s:(\d+):/);
                    if (!lengthMatch) break;
                    
                    const byteLength = parseInt(lengthMatch[1], 10);
                    pos += lengthMatch[0].length; // Move past "s:BYTE_LENGTH:"
                    
                    // Expect opening quote
                    if (content[pos] !== '"') break;
                    pos++; // Skip opening quote
                    
                    // Consume exactly byteLength UTF-8 bytes by walking characters
                    let bytesConsumed = 0;
                    let strStart = pos;
                    while (pos < content.length && bytesConsumed < byteLength) {
                        const code = content.codePointAt(pos);
                        // Determine UTF-8 byte width of this code point
                        if (code < 0x80) {
                            bytesConsumed += 1;
                            pos += 1;
                        } else if (code < 0x800) {
                            bytesConsumed += 2;
                            pos += 1;
                        } else if (code < 0x10000) {
                            bytesConsumed += 3;
                            pos += 1;
                        } else {
                            // Surrogate pair (code > 0xFFFF): 4 UTF-8 bytes, 2 JS chars
                            bytesConsumed += 4;
                            pos += 2;
                        }
                    }
                    const stringValue = content.substring(strStart, pos);
                    
                    // Expect closing quote
                    if (content[pos] !== '"') break;
                    pos++; // Skip closing quote
                    
                    // Expect semicolon
                    if (content[pos] !== ';') break;
                    pos++; // Skip semicolon
                    
                    result.push(stringValue);
                    
                } else if (content.substring(pos).startsWith('i:')) {
                    // Integer value: i:VALUE;
                    const intMatch = content.substring(pos).match(/^i:(\d+);/);
                    if (!intMatch) break;
                    
                    result.push(intMatch[1]); // Store as string for display
                    pos += intMatch[0].length;
                }
            }
            
            return result.length > 0 ? result : [];
        }
        
        // Handle simple string format like "item1,item2,item3"
        if (serializedString.includes(',') && !serializedString.includes('{')) {
            return serializedString.split(',').map(item => item.trim()).filter(item => item);
        }
        
        return [];
    } catch (error) {
        console.warn('Failed to unserialize PHP array:', error);
        return [];
    }
};

/**
 * Safe array parser that handles multiple formats
 */
export const parseArraySafely = (value, defaultValue = []) => {
    if (!value) return defaultValue;
    if (Array.isArray(value)) return value;
    
    if (typeof value === 'string') {
        // Try PHP unserialize first
        const unserialized = unserializePhpArray(value);
        if (unserialized.length > 0) return unserialized;
        
        // Try simple split
        if (value.includes(',')) {
            return value.split(',').map(item => item.trim()).filter(item => item);
        }
        
        // Single item
        return [value];
    }
    
    if (typeof value === 'object' && value !== null) {
        return Object.values(value);
    }
    
    return defaultValue;
};

/**
 * Parse numeric array (for quantities, amounts, etc.)
 */
export const parseNumericArray = (value, defaultValue = []) => {
    const parsed = parseArraySafely(value, defaultValue);
    return parsed.map(item => {
        const num = parseFloat(item);
        return isNaN(num) ? 0 : num;
    });
};
